import type { TPaymentStatus, TReservationStatus } from '@/utils/reservations';
import type { ApiResponse } from 'apisauce';
import type { Api } from './api';
import type { TConsumer } from './consumer-api';
import type { TOrganizationFAQ } from './organization-api';
import type { JSONContent } from '@tiptap/core';

/**
 * @deprecated
 * This type is deprecated and should not be used in new code.
 */
export type TOrganizationService = {
  id: string;
  name: string;
  price: number;
  reservationPct: number;
  active: boolean;
  description: string | null;
  isExtension: boolean;
  maxConsumers: number;
  showOnlyInLanding: boolean;
  minConsumers: number;
};

export type getServicesType = {
  total: number;
  limit: number;
  offset: number;
  page: number;
  hasNextPage: boolean;
  results: Service[];
};

export type getServiceType = {
  total: number;
  limit: number;
  offset: number;
  page: number;
  hasNextPage: boolean;
  results: Service[];
};

type Slot = {
  from: string; // "HH:MM"
  to: string; // "HH:MM"
  status: string;
  statusDetail?: string;
};

export type availableDateType = {
  date: string; // "DD-MM-YYYY"
  slots: Slot[];
};

type PaymentType = {
  id: string;
  name: string;
  description: string;
};

type PaymentStatus = {
  id: string;
  name: TPaymentStatus;
  description: string;
};

export type Payment = {
  createdBy: string;
  updatedBy: string;
  createdAt: string;
  updatedAt: string;
  active: boolean;
  id: string;
  reference: string;
  paymentType: PaymentType;
  paymentStatus: PaymentStatus;
  amount: number;
  observations: string | null;
  paidAt: string | null;
};

export type TReservation = {
  enabled: boolean;
  endTime: number;
  id: string;
  name: string;
  order: number;
  serviceDivisionGroup: Pick<ServiceDivisionGroup, 'id' | 'name'>;
  serviceDivision: Division;
  startTime: number;
  status: TReservationStatus;
};
interface User {
  id: string;
  firstName: string;
  lastName: string;
}

type Sport = {
  active: boolean;
  createdAt: string;
  createdBy?: string;
  description: string;
  id: string;
  name: string;
  updatedAt?: string;
  updatedBy?: string;
};

export type Division = {
  active: boolean;
  createdAt: string;
  createdBy?: string | null;
  description?: string | null;
  duration: number;
  id: string;
  name: string;
  order: number;
  quantity: number;
  startTime: string;
  endTime: string;
  startHour: string; // "HH:MM"
  updatedAt?: string | null;
  updatedBy?: string | null;
};

export type ServiceDivisionGroup = {
  active: boolean;
  createdAt: string;
  createdBy?: string | null;
  description?: string | null;
  divisions: Division[];
  id: string;
  name: string;
  updatedAt?: string | null;
  updatedBy?: string | null;
};

export type Service = {
  active: boolean;
  activeDays: string; // JSON string
  activeMonths: string; // JSON string
  allowPayment: 'all' | 'admin';
  canUpdateReservations: 'all' | 'admin';
  code: string | null;
  createdAt: string;
  createdBy: string | null;
  description: string;
  enabled: boolean;
  id: string;
  isExtension: boolean;
  maxAge: number;
  maxConsumers: number;
  minAge: number;
  minConsumers: number;
  name: string;
  organization?: TOrganizationFAQ; // Si pido un servicio por ID, trae la org
  price: number;
  reservationPct: number;
  serviceDivisionGroups: ServiceDivisionGroup[];
  rangeDivisions: Division[];
  serviceDivisions: Division[];
  showOnlyInLanding: boolean;
  sport: Sport;
  updatedAt: string | null;
  updatedBy: string | null;
  visibility: 'all' | 'admin';
  imageUrl: string | null;
  thumbnailUrl: string | null;
  // nextSessionDuration: number; // ??
  // remainingTime: number; // ??
  landing_page_content: JSONContent | null;
  completedPct: number; // ??
  activeDivisionType: 'default' | 'range';
};
interface TSaleStatus {
  name: 'paymentPending' | 'paymentComplete' | 'canceled';
  observation?: string;
}
type TSaleItem = {
  active: boolean;
  createdAt: string;
  createdBy: string | null;
  description: string;
  id: string;
  minPreReservationTime: number;
  name: string;
  price: number;
  quantity: number;
  reservationPct: number;
  service: Service;
  updatedAt: string;
  updatedBy: string | null;
};
export type myServicesData = {
  createdBy: string;
  updatedBy: string;
  createdAt: string;
  updatedAt: string;
  active: boolean;
  id: string;
  user: User;
  service: Service;
  validUntil: string | null;
  participants: TConsumer[];
  reservations: TReservation[];
  payments: Payment[];
  amountPending: number;
  totalAmount: number;
  organization: TOrganizationFAQ;
  acceptedTos: boolean;
  status: TSaleStatus;
  saleItems: TSaleItem[];
};

type getMyServicesType = {
  total: number;
  limit: number;
  offset: number;
  page: number;
  hasNextPage: boolean;
  results: myServicesData[];
};

export type TCategoryServices = {
  sport: {
    id: string;
    name: string;
  };
  services: Service[];
};

export class ServicesApi {
  readonly api: Api;

  constructor(api: Api) {
    this.api = api;
  }

  async getOrganization(organizationId: string): Promise<Service[]> {
    const response: ApiResponse<getServicesType> = await this.api.apisauce.get(
      'service?organization=' + organizationId
    );
    if (!response.ok) {
      throw response.originalError;
    }
    return response.data?.results || [];
  }

  async getMyService(): Promise<myServicesData[]> {
    await this.api.getRequestConfig();
    const response: ApiResponse<getMyServicesType> =
      await this.api.apisauce.get(`sale?sortBy=createdAt&sortDirection=DESC`);
    if (!response.ok) {
      throw response.originalError;
    }
    return response.data?.results || [];
  }
  async getSale(saleId: string): Promise<myServicesData | undefined> {
    await this.api.getRequestConfig();
    const response: ApiResponse<myServicesData> = await this.api.apisauce.get(
      `sale/${saleId}`
    );
    if (!response.ok) {
      throw response.originalError;
    }
    return response.data;
  }

  async getAvailability({
    serviceId,
    participants,
    month,
    year
  }: {
    serviceId: string;
    participants: number;
    month: number;
    year: number;
  }): Promise<availableDateType[] | undefined> {
    const response: ApiResponse<availableDateType[]> =
      await this.api.apisauce.get(
        `service/schedule?service=${serviceId}&consumerQty=${participants}&month=${month}&year=${year}`
      );
    if (!response.ok) {
      throw response.originalError;
    }
    return response.data;
  }
  async getOrganizationCategoriesWithServices(
    organizationId: string
  ): Promise<TCategoryServices[]> {
    const response: ApiResponse<TCategoryServices[]> =
      await this.api.apisauce.get(
        `service/sport?organization=${organizationId}`
      );
    if (!response.ok) {
      throw response.originalError;
    }
    return response.data || [];
  }
  async getServiceById(serviceId: string): Promise<Service | undefined> {
    const response: ApiResponse<Service> = await this.api.apisauce.get(
      `service/${serviceId}`
    );
    if (!response.ok) {
      throw response.originalError;
    }
    return response?.data;
  }
  async getServiceByCode(
    serviceCode: string,
    organizationCode: string
  ): Promise<Service | undefined> {
    const response: ApiResponse<getServiceType> = await this.api.apisauce.get(
      `service?filter.code=string.eq.${serviceCode}&filter.organization.code=string.eq.${organizationCode}`
    );
    if (!response.ok) {
      throw response.originalError;
    }
    return response.data?.results[0];
  }
}
